/**
 * GA4Editor - Configuration editor for Google Analytics 4
 *
 * UI is intentionally minimal: only Measurement ID. The previous send_page_view
 * and debug_mode toggles were removed because:
 * - send_page_view only controls gtag's auto page_view on funnel load (a single
 *   built-in event with the iframe URL — not our canonical screen events). In an
 *   SPA-iframe architecture it just adds noise; we hardcode `send_page_view: false`
 *   in the loader.
 * - debug_mode as a project-level toggle is a footgun: enabling it routes ALL
 *   property's events to DebugView and EXCLUDES them from standard reports
 *   (Realtime/Engagement/Explore). Instead we offer two opt-in mechanisms that
 *   are scoped to a single browser session: (1) `?debug_mode=1` URL param —
 *   share a URL with QA / on-call to inspect ONE user session in DebugView;
 *   (2) Google Analytics Debugger Chrome extension — works orthogonally at the
 *   network layer (adds _dbg=1 to outgoing /g/collect requests).
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, TextField, Button, Link } from '@mui/material';
import type { GA4Config, PlatformConfig } from '../../types/analytics.types';
import { ANALYTICS_SETTINGS_TEST_IDS } from '@modules/analytics-settings/testIds';

interface GA4EditorProps {
  config: PlatformConfig;
  onChange: (config: PlatformConfig, name?: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  showSaveButton?: boolean;
  onSave?: (config: PlatformConfig, name?: string) => void;
  name?: string;
}

const MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]+$/;

export const GA4Editor: React.FC<GA4EditorProps> = ({
  config,
  onChange,
  disabled = false,
  readOnly = false,
  showSaveButton = false,
  onSave,
  name,
}) => {
  const ga4Config = config as GA4Config;
  const [localConfig, setLocalConfig] = useState<GA4Config>(ga4Config);
  const [localName, setLocalName] = useState(name || '');

  useEffect(() => {
    setLocalConfig(ga4Config);
    setLocalName(name || '');
  }, [ga4Config, name]);

  const measurementIdError = useMemo(() => {
    const id = (localConfig.measurementId || '').trim();
    if (!id) return null; // empty is handled by `required` on the field
    return MEASUREMENT_ID_PATTERN.test(id)
      ? null
      : 'Invalid format. Must be G-XXXXXXXXXX (uppercase letters and digits only).';
  }, [localConfig.measurementId]);

  const handleChange = useCallback(
    (field: keyof GA4Config, value: unknown) => {
      const newConfig = { ...localConfig, [field]: value };
      setLocalConfig(newConfig);
      if (!showSaveButton) {
        onChange(newConfig, localName);
      }
    },
    [localConfig, localName, onChange, showSaveButton]
  );

  const handleSave = useCallback(() => {
    if (onSave && !measurementIdError) {
      onSave(localConfig, localName);
    }
  }, [onSave, localConfig, localName, measurementIdError]);

  const isValid = !!localConfig.measurementId && !measurementIdError;

  return (
    <Box>
      <TextField
        label="Measurement ID"
        value={localConfig.measurementId || ''}
        onChange={(e) => handleChange('measurementId', e.target.value.trim())}
        disabled={disabled || readOnly}
        fullWidth
        required
        error={!!measurementIdError}
        sx={{ mb: 2 }}
        placeholder="G-XXXXXXXXXX"
        helperText={
          measurementIdError ?? (
            <>
              Find your Measurement ID in{' '}
              <Link
                href="https://support.google.com/analytics/answer/9539598"
                target="_blank"
                rel="noopener"
              >
                GA4 Admin → Data Streams
              </Link>
            </>
          )
        }
        slotProps={{ htmlInput: { 'data-testid': ANALYTICS_SETTINGS_TEST_IDS.editorField('ga4', 'measurementId') } }}
      />

      {showSaveButton && (
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={disabled || !isValid}
            data-testid={ANALYTICS_SETTINGS_TEST_IDS.editorSave('ga4')}
          >
            Save
          </Button>
        </Box>
      )}
    </Box>
  );
};
